import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";

const navLinks = [
  { name: "Accueil", href: "#accueil" },
  { name: "Services", href: "#services" },
  { name: "À propos", href: "#apropos" },
  { name: "Nos centres", href: "#centres" },
  { name: "Contact", href: "#contact" },
];

export const Navbar = ({ mobileMenuOpen, setMobileMenuOpen }) => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <>
      <motion.nav
        data-testid="navbar"
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled 
            ? "bg-white/95 backdrop-blur-md shadow-lg" 
            : "bg-white/80 backdrop-blur-sm"
        }`}
      >
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="flex items-center justify-between h-20 md:h-24">
            {/* Logo */}
            <a 
              href="#accueil" 
              data-testid="logo-link"
              className="flex items-center"
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_site-creator-1560/artifacts/bqfdt7aq_02_logo_fond_transparent.png"
                alt="Potentiel+ Logo"
                className="h-16 md:h-20 w-auto object-contain"
              />
            </a>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              {navLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  data-testid={`nav-link-${link.name.toLowerCase().replace(/\s/g, '-')}`}
                  className="nav-link text-sm font-medium text-blue-900/70 hover:text-orange-500 transition-colors"
                >
                  {link.name}
                </a>
              ))}
            </div>

            {/* CTA Button Desktop */}
            <a
              href="tel:+33664446823"
              data-testid="cta-phone-btn"
              className="hidden md:flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-5 py-2.5 rounded-full text-sm font-semibold transition-all hover:scale-105 shadow-md hover:shadow-lg"
            >
              <Phone className="w-4 h-4" />
              <span>06 64 44 68 23</span>
            </a>

            {/* Mobile actions: call + menu */}
            <div className="flex items-center gap-1 md:hidden">
              <a
                href="tel:+33664446823"
                data-testid="mobile-call-icon-btn"
                aria-label="Appeler Potentiel+"
                className="flex items-center justify-center w-11 h-11 rounded-full bg-orange-500 text-white shadow-md active:scale-95 transition-transform"
              >
                <Phone className="w-5 h-5" />
              </a>
              <button
                data-testid="mobile-menu-btn"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="flex items-center justify-center w-11 h-11 text-blue-600 z-50 relative"
                aria-label="Toggle menu"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-7 h-7" />}
              </button>
            </div>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.2 }}
            className="fixed top-20 left-0 right-0 z-40 bg-white border-b border-gray-100 shadow-lg md:hidden max-h-[calc(100vh-5rem)] overflow-y-auto"
          >
            <div className="container mx-auto px-4 py-5">
              <div className="flex flex-col gap-1">
                {navLinks.map((link) => (
                  <a
                    key={link.name}
                    href={link.href}
                    data-testid={`mobile-nav-link-${link.name.toLowerCase().replace(/\s/g, '-')}`}
                    onClick={() => setMobileMenuOpen(false)}
                    className="text-lg font-medium text-blue-900 hover:text-orange-500 transition-colors py-3 border-b border-blue-50"
                  >
                    {link.name}
                  </a>
                ))}
                <a
                  href="/english-class"
                  data-testid="mobile-nav-link-english-class"
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-lg font-medium text-purple-700 hover:text-orange-500 transition-colors py-3 border-b border-blue-50"
                >
                  English Class
                </a>
                <a
                  href="tel:+33664446823"
                  data-testid="mobile-cta-phone-btn"
                  className="flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-5 py-3.5 rounded-full text-base font-semibold transition-all mt-4"
                >
                  <Phone className="w-4 h-4" />
                  <span>06 64 44 68 23</span>
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
